// Frontend permission utilities matching backend role system
// Source: backend/utils/permissions.js

const ROLE_HIERARCHY = {
  client: 1,
  trainer: 2,
  organization_admin: 3,
  platform_admin: 4,
};

const ROLE_NAMES = {
  client: 'Client',
  trainer: 'Trainer',
  organization_admin: 'Organization Admin',
  platform_admin: 'Platform Admin',
};

const ROLE_ICONS = {
  client: '👤',
  trainer: '💪',
  organization_admin: '🏢',
  platform_admin: '👑',
};

const ROLE_COLORS = {
  client: '#6B7280', // gray-500
  trainer: '#10B981', // green-500
  organization_admin: '#3B82F6', // blue-500
  platform_admin: '#8B5CF6', // purple-500
};

/**
 * Check if user has minimum required role level
 * @param {string} userRole - Current user's role
 * @param {string} requiredRole - Minimum required role
 * @returns {boolean}
 */
export const hasMinimumRole = (userRole, requiredRole) => {
  const userLevel = ROLE_HIERARCHY[userRole] || 0;
  const requiredLevel = ROLE_HIERARCHY[requiredRole] || 0;
  return userLevel >= requiredLevel;
};

/**
 * Check if user has exact role
 * @param {string} userRole - Current user's role
 * @param {string} requiredRole - Exact required role
 * @returns {boolean}
 */
export const hasExactRole = (userRole, requiredRole) => {
  return userRole === requiredRole;
};

/**
 * Check if user has any of the specified roles
 * @param {string} userRole - Current user's role
 * @param {string[]} allowedRoles - Array of allowed roles
 * @returns {boolean}
 */
export const hasAnyRole = (userRole, allowedRoles) => {
  return allowedRoles.includes(userRole);
};

/**
 * Check if a manager can manage a target user
 * Platform admin can manage anyone
 * Organization admin can manage trainers and clients
 * Trainer can only manage clients
 * @param {string} managerRole - Manager's role
 * @param {string} targetRole - Target user's role
 * @returns {boolean}
 */
export const canManageUser = (managerRole, targetRole) => {
  // Platform admin can manage anyone
  if (managerRole === 'platform_admin') {
    return true;
  }

  // Organization admin can manage trainers and clients
  if (managerRole === 'organization_admin') {
    return ['trainer', 'client'].includes(targetRole);
  }

  // Trainer can only manage clients
  if (managerRole === 'trainer') {
    return targetRole === 'client';
  }

  // Clients can't manage anyone
  return false;
};

/**
 * Get user-friendly role name
 * @param {string} role - Role to get name for
 * @returns {string}
 */
export const getRoleName = (role) => {
  return ROLE_NAMES[role] || role;
};

/**
 * Get role icon emoji
 * @param {string} role - Role to get icon for
 * @returns {string}
 */
export const getRoleIcon = (role) => {
  return ROLE_ICONS[role] || '👤';
};

/**
 * Get role color
 * @param {string} role - Role to get color for
 * @returns {string}
 */
export const getRoleColor = (role) => {
  return ROLE_COLORS[role] || '#6B7280';
};

/**
 * Get all roles with their hierarchy level (for dropdowns, etc.)
 * @returns {Array<{value: string, label: string, level: number, icon: string, color: string}>}
 */
export const getAllRoles = () => {
  return Object.entries(ROLE_HIERARCHY).map(([role, level]) => ({
    value: role,
    label: getRoleName(role),
    level,
    icon: getRoleIcon(role),
    color: getRoleColor(role),
  }));
};

/**
 * Check if user can access a specific feature based on role
 * @param {string} userRole - Current user's role
 * @param {string} feature - Feature name to check
 * @returns {boolean}
 */
export const canAccessFeature = (userRole, feature) => {
  const featurePermissions = {
    // Clients can only access basic features
    basic_features: [
      'client',
      'trainer',
      'organization_admin',
      'platform_admin',
    ],

    // Trainer features
    trainer_dashboard: ['trainer', 'organization_admin', 'platform_admin'],
    manage_clients: ['trainer', 'organization_admin', 'platform_admin'],
    create_programs: ['trainer', 'organization_admin', 'platform_admin'],

    // Organization admin features
    manage_organization: ['organization_admin', 'platform_admin'],
    manage_trainers: ['organization_admin', 'platform_admin'],

    // Platform admin features
    platform_settings: ['platform_admin'],
    manage_organizations: ['platform_admin'],
    view_all_data: ['platform_admin'],
  };

  return featurePermissions[feature]?.includes(userRole) || false;
};

export default {
  ROLE_HIERARCHY,
  hasMinimumRole,
  hasExactRole,
  hasAnyRole,
  canManageUser,
  getRoleName,
  getRoleIcon,
  getRoleColor,
  getAllRoles,
  canAccessFeature,
};
